import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
}

interface ToastContextType {
  toasts: Toast[];
  showToast: (type: ToastType, message: string, duration?: number) => void;
  dismissToast: (id: string) => void;
}

/** @graph-connects none */
const ToastContext = createContext<ToastContextType | null>(null);

/** @graph-connects none */
const typeStyles = {
  success:
    'bg-[var(--color-success-muted)] border-[var(--color-success)]/30 text-[var(--color-success)]',
  error: 'bg-[var(--color-error-muted)] border-[var(--color-error)]/30 text-[var(--color-error)]',
  warning:
    'bg-[var(--color-warning-muted)] border-[var(--color-warning)]/30 text-[var(--color-warning)]',
  info: 'bg-[var(--color-info-muted)] border-[var(--color-info)]/30 text-[var(--color-info)]',
};

/** @graph-connects none */
const typeIcons = {
  success: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  ),
  error: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  warning: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  ),
  info: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  ),
};

/**
 * @graph-stack cortex-admin
 * @graph-domain UX
 * @graph-business 個別トースト通知アイテム。種別アイコン・メッセージ・閉じるボタンをアニメーション付きで表示
 * @graph-connects none
 */
function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      className={`
        flex items-center gap-3 px-4 py-3
        rounded-lg border backdrop-blur-sm
        shadow-lg
        animate-slide-in-right
        ${typeStyles[toast.type]}
      `}
    >
      {typeIcons[toast.type]}
      <p className="flex-1 text-sm font-medium text-[var(--color-text-primary)]">{toast.message}</p>
      <button
        onClick={onDismiss}
        className="p-1 rounded hover:bg-white/10 transition-colors"
        aria-label={t('common.close')}
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  );
}

/**
 * @graph-stack cortex-admin
 * @graph-domain Engineering
 * @graph-business トースト通知プロバイダー。success/error/warning/info型の一時通知をContext経由で子コンポーネントに提供
 * @graph-connects none
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((type: ToastType, message: string, duration?: number) => {
    const id = Math.random().toString(36).substring(2, 11);
    const toast: Toast = { id, type, message, duration };

    setToasts((prev) => [...prev, toast]);

    if (duration != null && duration > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, duration);
    }
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, showToast, dismissToast }}>
      {children}
      {/* Toast Container */}
      <div
        className="fixed bottom-4 right-4 z-[var(--z-toast)] flex flex-col gap-2 max-w-sm w-full pointer-events-none"
        aria-live="polite"
        aria-atomic="true"
      >
        {toasts.map((toast) => (
          <div key={toast.id} className="pointer-events-auto">
            <ToastItem
              toast={toast}
              onDismiss={() => {
                dismissToast(toast.id);
              }}
            />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/**
 * @graph-stack cortex-admin
 * @graph-domain Engineering
 * @graph-business トースト通知フック。ToastProviderのContextからshowToast/dismissToastメソッドを取得するアクセサ
 * @graph-connects none
 */
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
